import { useEffect, useMemo, useState, Fragment } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronRight, Download, Search, ArrowUp, ArrowDown, Copy, X, Percent, AlertTriangle, Upload, Check, Pencil, Trash2, Landmark, CarFront, MoreVertical } from "lucide-react";
import { computeContractMetrics, type ScheduleInstallment } from "@/lib/financiacion/computeMetrics";
import type { EnrichedContract, RawContract } from "@/lib/financiacion/types";
import { exportContractsToExcel } from "@/lib/financiacion/exportFinanciacion";
import ScheduleTable from "./ScheduleTable";
import ImportScheduleDialog from "./ImportScheduleDialog";
import ContractAccountsEditor, { type ContractAccounts } from "./ContractAccountsEditor";

interface Props {
  kind: "leasing" | "loan";
}

type SortKey =
  | "company_name" | "contract_number" | "supplier" | "initial"
  | "pendingCap" | "pendingInt" | "avg" | "nextDate" | "status";

const fmt = (n: number) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const ContractsTable = ({ kind }: Props) => {
  const { toast } = useToast();
  const isLoan = kind === "loan";
  const colCount = 6;
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<EnrichedContract[]>([]);
  const [supplierNames, setSupplierNames] = useState<Map<string, string>>(new Map());
  /** contrato.id -> fecha de la última factura procesada que casa con una cuota de su cuadro */
  const [lastInvoiceByContract, setLastInvoiceByContract] = useState<Map<string, string>>(new Map());
  /** `${company_id}:${nifNormalizado}` -> contrapartidas por defecto del proveedor */
  const [accountDefaults, setAccountDefaults] = useState<Map<string, ContractAccounts>>(new Map());
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortKey>("company_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState({ contract_number: "", description: "", entity: "", plate_number: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EnrichedContract | null>(null);

  const startEdit = (c: EnrichedContract) => {
    setEditingId(c.id);
    setEditFields({
      contract_number: c.raw.contract_number || "",
      description: c.raw.description || "",
      entity: (isLoan ? c.raw.bank_name : c.raw.supplier_tax_id) || "",
      plate_number: c.raw.plate_number || "",
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editFields.contract_number.trim()) {
      toast({ title: "El nº de contrato es obligatorio", variant: "destructive" });
      return;
    }
    setSavingEdit(true);
    const payload: Record<string, any> = {
      contract_number: editFields.contract_number.trim(),
      description: editFields.description.trim() || null,
    };
    if (isLoan) payload.bank_name = editFields.entity.trim() || null;
    else {
      payload.supplier_tax_id = editFields.entity.trim() || "";
      payload.plate_number = editFields.plate_number.trim() || "";
    }

    const { error } = await supabase
      .from(isLoan ? "loan_contracts" : "leasing_contracts")
      .update(payload)
      .eq("id", editingId);
    setSavingEdit(false);
    if (error) {
      toast({ title: "No se pudo guardar", description: error.message, variant: "destructive" });
      return;
    }
    setContracts((prev) => prev.map((c) => (c.id === editingId ? { ...c, raw: { ...c.raw, ...payload } } : c)));
    setEditingId(null);
    toast({ title: "Contrato actualizado" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const { error: sErr } = await supabase
      .from(isLoan ? "loan_schedule" : "leasing_schedule")
      .delete()
      .eq("contract_id", id);
    if (sErr) {
      toast({ title: "No se pudo eliminar el cuadro", description: sErr.message, variant: "destructive" });
      return;
    }
    const { error } = await supabase
      .from(isLoan ? "loan_contracts" : "leasing_contracts")
      .delete()
      .eq("id", id);
    if (error) {
      toast({ title: "No se pudo eliminar", description: error.message, variant: "destructive" });
      return;
    }
    setContracts((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    if (expandedId === id) setExpandedId(null);
    setDeleteTarget(null);
    toast({ title: "Tabla eliminada", description: "Contrato y cuadro de amortización borrados." });
  };

  const addInstallment = async (contractId: string, row: ScheduleInstallment): Promise<boolean> => {
    const payload: Record<string, unknown> = {
      contract_id: contractId,
      installment_number: row.installment_number,
      due_date: row.due_date,
      insurance: row.insurance,
      total_amount: row.total,
      pending_capital: row.pending_capital ?? null,
    };
    if (isLoan) {
      payload.principal_amount = row.principal;
      payload.interest_amount = row.interest;
    } else {
      payload.cost_recovery = row.principal;
      payload.financial_charge = row.interest;
    }
    const { error } = await supabase
      .from(isLoan ? "loan_schedule" : "leasing_schedule")
      .insert(payload as never);
    if (error) {
      toast({ title: "No se pudo añadir la cuota", description: error.message, variant: "destructive" });
      return false;
    }
    setContracts((prev) =>
      prev.map((c) => {
        if (c.id !== contractId) return c;
        const schedule = [...c.schedule, row].sort((a, b) => a.installment_number - b.installment_number);
        return { ...c, schedule, metrics: computeContractMetrics(schedule, new Date(), c.raw.pending_capital ?? null) };
      }),
    );
    toast({ title: "Cuota añadida" });
    return true;
  };


  const normalizeNif = (nif: string): string =>
    (nif || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

  const contractAccounts = (c: EnrichedContract): ContractAccounts => {
    const base = accountDefaults.get(`${c.company_id}:${normalizeNif(c.raw.supplier_tax_id || "")}`) || {
      costRecovery: "", financialCharge: "", insurance: "",
    };
    return {
      costRecovery: c.raw.cost_recovery_account || base.costRecovery || "",
      financialCharge: c.raw.financial_charge_account || base.financialCharge || "",
      insurance: c.raw.insurance_account || base.insurance || "",
    };
  };

  /** Clave única de proveedor/entidad (evita duplicados por variantes de nombre) */
  const getSupplierKey = (c: EnrichedContract): string => {
    if (isLoan) {
      return (
        normalizeNif(c.raw.bank_tax_id || "") ||
        (c.raw.bank_name || "").replace(/[\s.,]/g, "").toUpperCase()
      );
    }
    return normalizeNif(c.raw.supplier_tax_id || "");
  };

  const getSupplierLabel = (c: EnrichedContract): string => {
    if (isLoan) return c.raw.bank_name || "";
    const nif = c.raw.supplier_tax_id || "";
    if (!nif) return "";
    const name = supplierNames.get(normalizeNif(nif));
    return name ? `${name} (${nif})` : nif;
  };


  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const contractsTable = isLoan ? "loan_contracts" : "leasing_contracts";
        const scheduleTable = isLoan ? "loan_schedule" : "leasing_schedule";

        const { data: cData, error: cErr } = await supabase
          .from(contractsTable)
          .select("*")
          .order("contract_number");
        if (cErr) throw cErr;
        const rawContracts = (cData || []) as RawContract[];

        if (rawContracts.length === 0) {
          if (mounted) { setContracts([]); setLoading(false); }
          return;
        }

        const companyIds = Array.from(new Set(rawContracts.map((c) => c.company_id)));
        const { data: companiesData } = await supabase
          .from("company_masters")
          .select("id, name")
          .in("id", companyIds);
        const companyMap = new Map<string, string>();
        (companiesData || []).forEach((c: any) => companyMap.set(c.id, c.name));

        const contractIds = rawContracts.map((c) => c.id);
        // Fetch schedule in chunks to avoid PostgREST 1000-row default cap
        const sData: any[] = [];
        const chunkSize = 10;
        for (let i = 0; i < contractIds.length; i += chunkSize) {
          const chunk = contractIds.slice(i, i + chunkSize);
          const { data: sPart, error: sErr } = await supabase
            .from(scheduleTable)
            .select("*")
            .in("contract_id", chunk)
            .range(0, 9999);
          if (sErr) throw sErr;
          if (sPart) sData.push(...sPart);
        }


        const scheduleByContract = new Map<string, ScheduleInstallment[]>();
        sData.forEach((row: any) => {
          const list = scheduleByContract.get(row.contract_id) || [];
          list.push({
            installment_number: row.installment_number,
            due_date: row.due_date,
            principal: Number(isLoan ? row.principal_amount : row.cost_recovery) || 0,
            interest: Number(isLoan ? row.interest_amount : row.financial_charge) || 0,
            insurance: Number(row.insurance) || 0,
            total: Number(row.total_amount) || 0,
            pending_capital: row.pending_capital != null ? Number(row.pending_capital) : null,
          });
          scheduleByContract.set(row.contract_id, list);
        });


        const enriched: EnrichedContract[] = rawContracts.map((c) => {
          const schedule = scheduleByContract.get(c.id) || [];
          return {
            id: c.id,
            company_id: c.company_id,
            company_name: companyMap.get(c.company_id) || "—",
            raw: c,
            schedule,
            metrics: computeContractMetrics(schedule, new Date(), c.pending_capital ?? null),
          };
        });

        // Load supplier names (razón social) for leasing tax_ids scoped by company
        if (!isLoan) {
          const pairs = rawContracts
            .map((c) => ({ company_id: c.company_id, nif: (c.supplier_tax_id || "").trim() }))
            .filter((p) => p.nif);
          if (pairs.length > 0) {
            const uniqNifs = Array.from(new Set(pairs.map((p) => p.nif)));
            const { data: supData } = await supabase
              .from("supplier_master")
              .select("company_id, tax_id, description")
              .in("company_id", companyIds)
              .in("tax_id", uniqNifs);
            // Un único nombre canónico por NIF: agrupamos las variantes
            // ("CAIXABANK SA", "CAIXABANK, S.A."…) y elegimos la más frecuente
            // (y en empate, la más completa).
            const variants = new Map<string, Map<string, number>>();
            (supData || []).forEach((s: any) => {
              const nif = (s.tax_id || "")
                .replace(/[\s.\-]/g, "")
                .toUpperCase()
                .replace(/^ES(?=[A-Z0-9]{9}$)/, "");
              const desc = (s.description || "").trim();
              if (!nif || !desc) return;
              if (!variants.has(nif)) variants.set(nif, new Map());
              const m = variants.get(nif);
              if (!m) return;
              m.set(desc, (m.get(desc) || 0) + 1);
            });
            const nameMap = new Map<string, string>();
            variants.forEach((m, nif) => {
              const best = Array.from(m.entries()).sort(
                (a, b) => b[1] - a[1] || b[0].length - a[0].length || a[0].localeCompare(b[0], "es"),
              )[0];
              if (best) nameMap.set(nif, best[0]);
            });
            if (mounted) setSupplierNames(nameMap);

          } else if (mounted) {
            setSupplierNames(new Map());
          }
        }

        if (!isLoan) {
          const { data: defData } = await supabase
            .from("supplier_leasing_defaults")
            .select("company_id, supplier_tax_id, cost_recovery_account, financial_charge_account, insurance_account")
            .in("company_id", companyIds);
          const defMap = new Map<string, ContractAccounts>();
          (defData || []).forEach((d: any) => {
            defMap.set(`${d.company_id}:${normalizeNif(d.supplier_tax_id || "")}`, {
              costRecovery: d.cost_recovery_account || "",
              financialCharge: d.financial_charge_account || "",
              insurance: d.insurance_account || "",
            });
          });
          if (mounted) setAccountDefaults(defMap);
        }

        // Trazabilidad: última factura procesada que casa con una cuota del cuadro.
        // Se empareja por empresa + NIF del proveedor y, dentro del mismo mes,
        // por importe de la cuota (tolerancia 1 €). Si el proveedor solo tiene un
        // contrato en esa empresa, basta con la coincidencia de mes.
        if (!isLoan) {
          const { data: histData } = await supabase
            .from("invoice_history")
            .select("company_id, supplier_tax_id, invoice_date, base_amount, total_amount")
            .in("company_id", companyIds);
          const norm = (s: string) =>
            (s || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");
          const contractsByKey = new Map<string, EnrichedContract[]>();
          enriched.forEach((c) => {
            const key = `${c.company_id}:${norm(c.raw.supplier_tax_id || "")}`;
            const list = contractsByKey.get(key) || [];
            list.push(c);
            contractsByKey.set(key, list);
          });
          const lastMap = new Map<string, string>();
          (histData || []).forEach((h: any) => {
            const key = `${h.company_id}:${norm(h.supplier_tax_id || "")}`;
            const candidates = contractsByKey.get(key);
            if (!candidates || !h.invoice_date) return;
            const month = String(h.invoice_date).slice(0, 7);
            const base = Number(h.base_amount) || 0;
            const total = Number(h.total_amount) || 0;
            for (const c of candidates) {
              const row = c.schedule.find((s) => String(s.due_date).slice(0, 7) === month);
              if (!row) continue;
              const amountOk =
                Math.abs(row.total - base) <= 1 || Math.abs(row.total - total) <= 1;
              if (!amountOk && candidates.length > 1) continue;
              const prev = lastMap.get(c.id);
              if (!prev || h.invoice_date > prev) lastMap.set(c.id, String(h.invoice_date));
              if (amountOk) break;
            }
          });
          if (mounted) setLastInvoiceByContract(lastMap);
        }



        if (mounted) setContracts(enriched);
      } catch (e: any) {
        toast({ title: "Error cargando datos", description: e.message, variant: "destructive" });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [kind, isLoan, toast, reloadKey]);

  const companies = useMemo(
    () => Array.from(new Set(contracts.map((c) => c.company_name))).sort(),
    [contracts],
  );
  const suppliers = useMemo(() => {
    // Una sola entrada por proveedor (clave = NIF normalizado)
    const map = new Map<string, string>();
    contracts.forEach((c) => {
      const key = getSupplierKey(c);
      const label = getSupplierLabel(c);
      if (!key || !label) return;
      if (!map.has(key)) map.set(key, label);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [contracts, isLoan, supplierNames]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = contracts.filter((c) => {
      if (companyFilter !== "all" && c.company_name !== companyFilter) return false;
      if (supplierFilter !== "all" && getSupplierKey(c) !== supplierFilter) return false;

      if (statusFilter !== "all" && c.metrics.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        c.company_name, c.raw.contract_number, c.raw.description,
        c.raw.plate_number, c.raw.supplier_tax_id, c.raw.bank_tax_id, c.raw.bank_name,
        getSupplierLabel(c),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const get = (c: EnrichedContract): string | number => {
        switch (sortBy) {
          case "company_name": return c.company_name;
          case "contract_number": return c.raw.contract_number || "";
          case "supplier": return getSupplierLabel(c);
          case "initial": return c.metrics.initialCapital;
          case "pendingCap": return c.metrics.pendingCapital;
          case "pendingInt": return c.metrics.pendingInterest;
          case "avg": return c.metrics.averageInstallment;
          case "nextDate": return c.metrics.nextInstallment?.due_date || "";
          case "status": return c.metrics.status;
        }
      };
      const va = get(a); const vb = get(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "es") * dir;
    });
    return list;
  }, [contracts, search, companyFilter, supplierFilter, statusFilter, sortBy, sortDir, isLoan, supplierNames]);

  // Prune stale selections when filters change
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visible = new Set(filtered.map((c) => c.id));
    let changed = false;
    const next = new Set<string>();
    selectedIds.forEach((id) => {
      if (visible.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelectedIds(next);
  }, [filtered, selectedIds]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };

  const SortHead = ({ k, children, className }: { k: SortKey; children: React.ReactNode; className?: string }) => (
    <TableHead className={className}>
      <Button variant="ghost" size="sm"
        onClick={() => toggleSort(k)}
        className="h-auto p-0 text-xs font-semibold hover:bg-transparent"
      >
        {children}
        {sortBy === k ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : null}
      </Button>
    </TableHead>
  );

  const handleExport = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      await exportContractsToExcel(filtered, kind, `${kind === "loan" ? "prestamos" : "leasings"}_${today}.xlsx`);
    } catch (e: any) {
      toast({ title: "Error exportando", description: e.message, variant: "destructive" });
    }
  };

  const statusBadge = (s: string) => {
    if (s === "finalizado") return <Badge variant="secondary">Finalizado</Badge>;
    if (s === "por_vencer") return <Badge variant="outline" className="bg-accent text-accent-foreground border-border">Por vencer</Badge>;
    return <Badge className="bg-success text-success-foreground border-transparent">Vigente</Badge>;
  };

  // Row selection helpers (Excel-like)
  const handleRowSelect = (id: string, e: React.MouseEvent) => {
    const shift = e.shiftKey;
    const meta = e.ctrlKey || e.metaKey;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shift && lastSelectedId) {
        const ids = filtered.map((c) => c.id);
        const a = ids.indexOf(lastSelectedId);
        const b = ids.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        }
      }
      if (meta) {
        if (next.has(id)) next.delete(id); else next.add(id);
      } else {
        if (next.size === 1 && next.has(id)) next.delete(id);
        else { next.clear(); next.add(id); }
      }
      return next;
    });
    setLastSelectedId(id);
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
  const someVisibleSelected = !allVisibleSelected && filtered.some((c) => selectedIds.has(c.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((c) => c.id)));
    }
    setLastSelectedId(null);
  };

  const selectionTotals = useMemo(() => {
    const rows = filtered.filter((c) => selectedIds.has(c.id));
    const t = { count: rows.length, initial: 0, pendingCap: 0, pendingInt: 0, avg: 0, nextAmount: 0 };
    rows.forEach((c) => {
      t.initial += c.metrics.initialCapital || 0;
      t.pendingCap += c.metrics.pendingCapital || 0;
      t.pendingInt += c.metrics.pendingInterest || 0;
      t.avg += c.metrics.averageInstallment || 0;
      t.nextAmount += c.metrics.nextInstallment?.total || 0;
    });
    return t;
  }, [filtered, selectedIds]);

  const copyTotals = async () => {
    const header = ["Filas", "Capital inicial", "Capital pendiente", "Intereses pdtes.", "Suma cuota media", "Suma próx. importe"].join("\t");
    const values = [
      selectionTotals.count,
      selectionTotals.initial.toFixed(2).replace(".", ","),
      selectionTotals.pendingCap.toFixed(2).replace(".", ","),
      selectionTotals.pendingInt.toFixed(2).replace(".", ","),
      selectionTotals.avg.toFixed(2).replace(".", ","),
      selectionTotals.nextAmount.toFixed(2).replace(".", ","),
    ].join("\t");
    try {
      await navigator.clipboard.writeText(`${header}\n${values}`);
      toast({ title: "Totales copiados", description: "Pega en Excel con Ctrl+V." });
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  return (
    <div className="p-4 space-y-4 pb-20">


      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={`Buscar ${isLoan ? "préstamo" : "contrato"}, matrícula, CIF, proveedor, cliente…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-8 text-sm"
          />
        </div>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="h-8 w-[220px] text-xs"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los clientes</SelectItem>
            {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supplierFilter} onValueChange={setSupplierFilter}>
          <SelectTrigger className="h-8 w-[200px] text-xs"><SelectValue placeholder={isLoan ? "Entidad" : "Proveedor"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isLoan ? "Todas las entidades" : "Todos los proveedores"}</SelectItem>
            {suppliers.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="vigente">Vigente</SelectItem>
            <SelectItem value="por_vencer">Por vencer (&lt;6m)</SelectItem>
            <SelectItem value="finalizado">Finalizado</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{filtered.length} contrato{filtered.length === 1 ? "" : "s"}</span>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} className="h-8 gap-1.5">
            <Upload className="h-3.5 w-3.5" /> Importar tabla
          </Button>
          <Button size="sm" variant="outline" onClick={handleExport} disabled={filtered.length === 0} className="h-8 gap-1.5">
            <Download className="h-3.5 w-3.5" /> Exportar Excel
          </Button>
        </div>
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table className="min-w-[640px] [&_td]:py-4">
          <TableHeader className="sticky top-0 z-10 bg-muted">
            <TableRow>
              <TableHead className="w-10 text-center">
                <Checkbox
                  checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Seleccionar todo"
                />
              </TableHead>
              <SortHead k="contract_number" className="w-[42%]">Nombre</SortHead>
              <SortHead k="status">Estado</SortHead>
              <TableHead className="text-xs w-[22%]">Progreso</TableHead>
              <SortHead k="nextDate">Próximo vencimiento</SortHead>
              <TableHead className="w-12"><span className="sr-only">Acciones</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-10">Cargando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-10">
                Sin contratos {isLoan ? "de préstamo" : "de leasing"}.
              </TableCell></TableRow>
            ) : filtered.map((c) => {
              const supplier = getSupplierLabel(c);

              const selected = selectedIds.has(c.id);
              const isEditing = editingId === c.id;
              return (
                <Fragment key={c.id}>
                  <TableRow
                    className={`h-9 ${selected ? "bg-primary/10 hover:bg-primary/15" : "hover:bg-accent/40 even:bg-muted/20"}`}
                  >
                    <TableCell
                      className={`text-center ${selected ? "border-l-2 border-l-primary" : ""}`}
                      onClick={(e) => { e.stopPropagation(); handleRowSelect(c.id, e); }}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                            return next;
                          });
                          setLastSelectedId(c.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Seleccionar fila"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-3">
                        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-primary"
                          onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                          aria-label={`Ver cuadro de ${c.raw.contract_number}`} aria-expanded={expandedId === c.id}>
                          {expandedId === c.id ? <ChevronDown className="h-4 w-4" /> : isLoan ? <Landmark className="h-4 w-4" /> : <CarFront className="h-4 w-4" />}
                        </Button>
                        <div className="min-w-0 flex-1 space-y-1">
                          {isEditing ? (
                            <div className="grid gap-2 sm:grid-cols-2">
                              <Input aria-label="Número de contrato" value={editFields.contract_number} onChange={(e) => setEditFields({ ...editFields, contract_number: e.target.value })} className="h-8 text-xs" />
                              <Input aria-label={isLoan ? "Entidad" : "NIF proveedor"} placeholder={isLoan ? "Entidad" : "NIF proveedor"} value={editFields.entity} onChange={(e) => setEditFields({ ...editFields, entity: e.target.value })} className="h-8 text-xs" />
                              <Input aria-label="Descripción" placeholder="Descripción" value={editFields.description} onChange={(e) => setEditFields({ ...editFields, description: e.target.value })} className="h-8 text-xs" />
                              {!isLoan && <Input aria-label="Matrícula" placeholder="Matrícula" value={editFields.plate_number} onChange={(e) => setEditFields({ ...editFields, plate_number: e.target.value })} className="h-8 text-xs" />}
                            </div>
                          ) : (
                            <>
                              <Button variant="link" className="h-auto max-w-full justify-start whitespace-normal p-0 text-left text-sm font-semibold text-foreground"
                                onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                                {c.raw.description || `${isLoan ? "Préstamo" : "Leasing"} ${c.raw.contract_number}`}
                              </Button>
                              <p className="text-xs text-muted-foreground break-words">{c.company_name}</p>
                              <p className="text-xs text-muted-foreground break-words">
                                {[supplier, c.raw.description ? c.raw.contract_number : null, !isLoan ? c.raw.plate_number : null].filter(Boolean).join(" · ")}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1.5">
                        {statusBadge(c.metrics.status)}
                        {c.metrics.needsMoreSchedule ? (
                          <Badge variant="outline" className="gap-1 text-destructive border-destructive/30 bg-destructive/10"><AlertTriangle className="h-3 w-3" />Pedir cuadro</Badge>
                        ) : c.metrics.scheduleIncomplete ? <Badge variant="secondary">Cuadro parcial</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="min-w-[110px] max-w-[220px] space-y-2" title="Progreso según las fechas del cuadro de amortización; no confirma pagos bancarios">
                        <Progress value={c.metrics.totalInstallments ? (c.metrics.paidInstallments / c.metrics.totalInstallments) * 100 : 0}
                          aria-label={`Progreso de ${c.raw.contract_number}`} className="h-1.5 bg-success/15 [&>div]:bg-success" />
                        <div className="flex justify-between gap-2 text-xs text-muted-foreground tabular-nums">
                          <span>{c.metrics.paidInstallments}/{c.metrics.totalInstallments}</span>
                          <span>{c.metrics.totalInstallments ? `${Math.round((c.metrics.paidInstallments / c.metrics.totalInstallments) * 100)}%` : "—"}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {c.metrics.nextInstallment ? (
                        <div className="space-y-1 whitespace-nowrap">
                          <p className="text-sm font-medium">{c.metrics.nextInstallment.due_date.split("-").reverse().join("/")}</p>
                          <p className="text-xs text-muted-foreground">{fmt(c.metrics.nextInstallment.total)} €</p>
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditingId(null)} aria-label="Cancelar"><X className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={saveEdit} disabled={savingEdit} aria-label="Guardar"><Check className="h-4 w-4" /></Button>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={`Acciones de ${c.raw.contract_number}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setExpandedId(expandedId === c.id ? null : c.id)}><ChevronRight className="mr-2 h-4 w-4" />{expandedId === c.id ? "Cerrar detalle" : "Ver detalle y cuotas"}</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => startEdit(c)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleteTarget(c)}><Trash2 className="mr-2 h-4 w-4" />Eliminar tabla</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedId === c.id && (
                    <TableRow>
                      <TableCell colSpan={colCount} className="bg-muted/30 p-4 space-y-4">
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs md:grid-cols-4">
                          {[
                            ["Capital inicial", `${fmt(c.metrics.initialCapital)} €`],
                            ["Capital pendiente", `${fmt(c.metrics.pendingCapital)} €`],
                            ["Intereses pendientes", `${fmt(c.metrics.pendingInterest)} €`],
                            ["Cuota media", `${fmt(c.metrics.averageInstallment)} €`],
                            ["Número de contrato", c.raw.contract_number],
                            [isLoan ? "Entidad" : "Proveedor", supplier || "—"],
                            ...(!isLoan ? [["Última factura", lastInvoiceByContract.get(c.id) || "Sin facturas"], ["Deducibilidad IVA", c.raw.vat_deduction_half ? "50%" : "Sin ajuste al 50%"]] : []),
                            ...(c.raw.pending_capital_date ? [["Capital pendiente a fecha", c.raw.pending_capital_date]] : []),
                          ].map(([label, value]) => (
                            <div key={label} className="min-w-0"><dt className="text-muted-foreground">{label}</dt><dd className="mt-1 font-medium break-words tabular-nums">{value}</dd></div>
                          ))}
                        </dl>
                        {!isLoan && (
                          <ContractAccountsEditor
                            contractId={c.id}
                            companyId={c.company_id}
                            supplierTaxId={c.raw.supplier_tax_id || ""}
                            contractAccounts={{
                              costRecovery: c.raw.cost_recovery_account || "",
                              financialCharge: c.raw.financial_charge_account || "",
                              insurance: c.raw.insurance_account || "",
                            }}
                            defaults={accountDefaults.get(`${c.company_id}:${normalizeNif(c.raw.supplier_tax_id || "")}`) || { costRecovery: "", financialCharge: "", insurance: "" }}
                            onSaved={({ contractAccounts, defaults }) => {
                              setContracts((prev) => prev.map((x) => (x.id === c.id
                                ? { ...x, raw: {
                                    ...x.raw,
                                    cost_recovery_account: contractAccounts.costRecovery,
                                    financial_charge_account: contractAccounts.financialCharge,
                                    insurance_account: contractAccounts.insurance,
                                  } }
                                : x)));
                              setAccountDefaults((prev) => {
                                const next = new Map(prev);
                                next.set(`${c.company_id}:${normalizeNif(c.raw.supplier_tax_id || "")}`, defaults);
                                return next;
                              });
                            }}
                          />

                        )}
                        <ScheduleTable schedule={c.schedule} isLoan={isLoan} onAdd={(row) => addInstallment(c.id, row)} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {selectionTotals.count > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t shadow-lg px-4 py-2 flex flex-wrap items-center justify-center gap-4 text-xs">
          <span className="font-semibold text-primary">{selectionTotals.count} fila{selectionTotals.count === 1 ? "" : "s"}</span>
          <span className="text-muted-foreground">Σ Capital inicial: <span className="font-mono tabular-nums text-foreground">{fmt(selectionTotals.initial)}</span></span>
          <span className="text-muted-foreground">Σ Pendiente: <span className="font-mono tabular-nums text-foreground">{fmt(selectionTotals.pendingCap)}</span></span>
          <span className="text-muted-foreground">Σ Intereses: <span className="font-mono tabular-nums text-foreground">{fmt(selectionTotals.pendingInt)}</span></span>
          <span className="text-muted-foreground">Σ Cuota: <span className="font-mono tabular-nums text-foreground">{fmt(selectionTotals.avg)}</span></span>
          <div className="flex items-center gap-1 ml-2 border-l pl-3">
            <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" onClick={copyTotals}>
              <Copy className="h-3 w-3" /> Copiar
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 gap-1" onClick={() => { setSelectedIds(new Set()); setLastSelectedId(null); }}>
              <X className="h-3 w-3" /> Limpiar
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta tabla?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrarán el contrato <strong>{deleteTarget?.raw.contract_number}</strong> de{" "}
              <strong>{deleteTarget?.company_name}</strong> y sus {deleteTarget?.schedule.length ?? 0} cuotas.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportScheduleDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        kind={kind}
        onImported={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
};

export default ContractsTable;
